import { createContext, useContext, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleHelp } from 'lucide-react';
import { HELP_AR, HELP_DE, HELP_EN, HELP_TR, type HelpContent, type HelpKey } from '@zt/help';

/** Paketle gelen yardım başlıkları (F7-17: de ve ar eklendi); ayrıntılar Türkçe kayıttan. */
const BUNDLED: Record<string, Partial<Record<HelpKey, Partial<HelpContent>>>> = {
  en: HELP_EN,
  de: HELP_DE,
  ar: HELP_AR,
};
import { cn, InsertBody, PageInsert, Popover, type InsertContent, type InsertLabels } from '@zt/ui';
import { api } from './api';
import { useI18n } from './i18n';
import type { HelpRow } from './types';

interface HelpCtx {
  get: (key: HelpKey) => InsertContent;
  labels: InsertLabels;
  beginner: boolean;
}

const Ctx = createContext<HelpCtx | null>(null);

/** Paketteki içerik temel alınır; giriş yapılmışsa panelden düzenlenmiş güncel metinler üzerine yazılır. */
export function HelpProvider({
  children,
  signedIn,
  uiMode,
}: {
  children: ReactNode;
  signedIn: boolean;
  uiMode: 'beginner' | 'advanced';
}) {
  const { locale, t } = useI18n();
  const remote = useQuery({
    queryKey: ['help', locale],
    queryFn: () =>
      // Varsayılan metinler paketle gelir; sunucudan yalnızca panelden düzenlenmiş kayıtlar alınır.
      api.get<HelpRow[]>(`/v1/admin/help?scope=customized&locale=${locale}`),
    enabled: signedIn,
    staleTime: 5 * 60_000,
  });
  const value = useMemo<HelpCtx>(() => {
    const byKey = new Map((remote.data ?? []).map((r) => [r.key, r]));
    return {
      beginner: uiMode === 'beginner',
      labels: {
        whatItDoes: t('admin.help.whatItDoes'),
        whenToUse: t('admin.help.whenToUse'),
        dataChanged: t('admin.help.dataChanged'),
        requiredPermission: t('admin.help.requiredPermission'),
        reversible: t('admin.help.reversible'),
        commonErrors: t('admin.help.commonErrors'),
        guide: t('admin.help.guide'),
      },
      get: (key) => {
        const r = byKey.get(key);
        if (r) return r;
        const base = HELP_TR[key] as HelpContent;
        return locale === 'tr' ? base : { ...base, ...(BUNDLED[locale] ?? HELP_EN)[key] };
      },
    };
  }, [remote.data, locale, t, uiMode]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useHelp() {
  const v = useContext(Ctx);
  if (!v) throw new Error('HelpProvider eksik');
  return v;
}

/**
 * Yardım simgesi (?). Fareyle üzerine gelince (masaüstü) veya dokununca (mobil) açılır.
 * `k` tip olarak yardım anahtarlarıyla sınırlıdır: açıklaması olmayan bir anahtar derlemeyi kırar.
 */
export function HelpTip({ k }: { k: HelpKey }) {
  const { get, labels } = useHelp();
  const { t } = useI18n();
  const content = get(k);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const finePointer = typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;
  const hoverProps = finePointer
    ? {
        onMouseEnter: () => {
          window.clearTimeout(closeTimer.current);
          setOpen(true);
        },
        onMouseLeave: () => {
          closeTimer.current = window.setTimeout(() => setOpen(false), 180);
        },
      }
    : {};
  // Masaüstünde fare üzerine gelince zaten açılır; ardından gelen tıklama onu kapatmamalı (aç/kapa yarışı).
  // Tıklama her zaman açık tutar; kapatma Esc, dışarı tıklama veya fareyi uzaklaştırmayla olur.
  const triggerProps = finePointer
    ? {
        ...hoverProps,
        onClick: (e: MouseEvent) => {
          e.preventDefault();
          window.clearTimeout(closeTimer.current);
          setOpen(true);
        },
      }
    : {};
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={content.title}
      trigger={
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-3 hover:bg-pine-tint hover:text-pine"
          aria-label={t('admin.help.open', { title: content.title })}
          data-help-key={k}
          {...triggerProps}
        >
          <CircleHelp className="h-4 w-4" aria-hidden />
        </button>
      }
    >
      <div className="border-s-[3px] border-insert-rule bg-insert p-4" {...hoverProps}>
        <p className="mb-2 font-cond text-[0.9375rem] font-semibold">{content.title}</p>
        <InsertBody content={content} labels={labels} compact />
      </div>
    </Popover>
  );
}

/** Sayfa çerçevesi: başlık (yardım kaydından), "Bu sayfa ne işe yarar?" prospektüsü, eylemler. */
export function PageFrame({
  helpKey,
  actions,
  children,
  title,
  wide,
  printable,
}: {
  helpKey: HelpKey;
  actions?: ReactNode;
  children: ReactNode;
  title?: string;
  /** Geniş çalışma alanı gereken ekranlar (ör. canlı önizlemeli tema editörü). */
  wide?: boolean;
  /** Yazdırılan sayfa (F5B-12): başlık, düğmeler ve sayfa yardımı kâğıda basılmaz. */
  printable?: boolean;
}) {
  const { get, labels, beginner } = useHelp();
  const { t } = useI18n();
  const content = get(helpKey);
  return (
    <div
      className={cn(
        'mx-auto w-full px-4 pb-16 pt-6 sm:px-8 sm:pt-10',
        wide ? 'max-w-[90rem]' : 'max-w-5xl',
        printable && 'print:max-w-none print:p-0',
      )}
    >
      <div className={cn('mb-5 flex flex-wrap items-end justify-between gap-3', printable && 'print:hidden')}>
        <h1 className="font-cond text-[1.75rem] font-semibold leading-tight tracking-[-0.01em] text-ink sm:text-[2rem]">
          {title ?? content.title}
        </h1>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      <div className={cn(printable && 'print:hidden')}>
        <PageInsert
          heading={t('common.whatIsThisPage')}
          content={content}
          labels={labels}
          defaultOpen={beginner}
        />
      </div>
      <div className="space-y-8">{children}</div>
    </div>
  );
}
